import React from "react";
import { t } from "@auto/services";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

import * as api from "../api";
import {
  selectEntity,
  setArrondissements,
  setCommunes,
  setError,
  setLoading,
  setSections,
  setVotingCenters,
  toggleExpanded,
} from "../state";
import { VIEW_ADMINISTRATIVE } from "../constants";
import { useObservable } from "../hooks";
import { canCreateGeographyEntity, canOpenGeographyContext } from "@src/security/permissions";

import TreeNode from "./TreeNode";

import styles from "../../ElectoralGeographyPage.module.styl";

const AdministrativeTree = ({ gui, state, setState, events }) => {
  const openContextMenu = (event, type, item) => {
    events.contextMenuOpened.publish({ event, type, item });
  };

  const loadBranchAfterSave = React.useCallback(async ({ entity }) => {
    if (state.viewMode !== VIEW_ADMINISTRATIVE) {
      return;
    }

    setLoading(setState, true);

    try {
      if (entity === "arrondissement" && state.selection.department?.gid) {
        setArrondissements(setState, await api.loadArrondissements(state.selection.department.gid));
        return;
      }

      if (entity === "commune" && state.selection.arrondissement?.gid) {
        setCommunes(setState, await api.loadCommunesByArrondissement(state.selection.arrondissement.gid));
        return;
      }

      if (entity === "section" && state.selection.commune?.gid) {
        setSections(setState, await api.loadSections(state.selection.commune.gid));
        return;
      }

      if (entity === "voting-center" && state.selection.section?.gid) {
        setVotingCenters(setState, await api.loadVotingCenters(state.selection.section.gid));
      }
    } catch (err) {
      setError(setState, err?.message || String(err));
    } finally {
      setLoading(setState, false);
    }
  }, [setState, state.selection.arrondissement, state.selection.commune, state.selection.department, state.selection.section, state.viewMode]);

  const selectDepartment = React.useCallback(async (department) => {
    const wasExpanded = state.expanded.department === department.gid;

    selectEntity(setState, "department", department);
    toggleExpanded(setState, "department", department.gid);

    if (!department?.gid || wasExpanded) {
      return;
    }

    setLoading(setState, true);

    try {
      setArrondissements(setState, await api.loadArrondissements(department.gid));
    } finally {
      setLoading(setState, false);
    }
  }, [setState, state.expanded.department]);

  const selectArrondissement = React.useCallback(async (arrondissement) => {
    const wasExpanded = state.expanded.arrondissement === arrondissement.gid;

    selectEntity(setState, "arrondissement", arrondissement);
    toggleExpanded(setState, "arrondissement", arrondissement.gid);

    if (!arrondissement?.gid || wasExpanded) {
      return;
    }

    setLoading(setState, true);

    try {
      setCommunes(setState, await api.loadCommunesByArrondissement(arrondissement.gid));
    } finally {
      setLoading(setState, false);
    }
  }, [setState, state.expanded.arrondissement]);

  const selectCommune = React.useCallback(async (commune) => {
    const wasExpanded = state.expanded.commune === commune.gid;

    selectEntity(setState, "commune", commune);
    toggleExpanded(setState, "commune", commune.gid);

    if (!commune?.gid || wasExpanded) {
      return;
    }

    setLoading(setState, true);

    try {
      setSections(setState, await api.loadSections(commune.gid));
    } finally {
      setLoading(setState, false);
    }
  }, [setState, state.expanded.commune]);

  const selectSection = React.useCallback(async (section) => {
    const wasExpanded = state.expanded.section === section.gid;

    selectEntity(setState, "section", section);
    toggleExpanded(setState, "section", section.gid);

    if (!section?.gid || wasExpanded) {
      return;
    }

    setLoading(setState, true);

    try {
      setVotingCenters(setState, await api.loadVotingCenters(section.gid));
    } finally {
      setLoading(setState, false);
    }
  }, [setState, state.expanded.section]);

  const selectVotingCenter = React.useCallback((votingCenter) => {
    selectEntity(setState, "voting-center", votingCenter);
  }, [setState]);

  useObservable(events.departmentSelected, selectDepartment);
  useObservable(events.arrondissementSelected, selectArrondissement);
  useObservable(events.communeSelected, selectCommune);
  useObservable(events.sectionSelected, selectSection);
  useObservable(events.votingCenterSelected, selectVotingCenter);
  useObservable(events.entitySaved, loadBranchAfterSave);
  useObservable(events.entityDeleted, loadBranchAfterSave);

  return (
    <>
      {canCreateGeographyEntity(gui, "department") ? (
        <button
          type="button"
          className={`${styles.treeNode} ${styles.rootAddNode}`}
          onClick={() => events.createRequested.publish({ entity: "department" })}
        >
          <span className={styles.treeTwisty} />
          <span className={styles.treeIcon}>
            <AddOutlinedIcon fontSize="small" />
          </span>
          <span className={styles.treeLabel}>{t("geo.addDepartment")}</span>
        </button>
      ) : null}

      {state.data.departments.map((department) => {
        const departmentExpanded = state.expanded.department === department.gid;

        return (
          <TreeNode
            key={department.gid}
            type="department"
            item={department}
            level={0}
            selected={state.selection.department?.gid === department.gid}
            expanded={departmentExpanded}
            hasChildren
            onClick={() => events.departmentSelected.publish(department)}
            onContextMenu={canOpenGeographyContext(gui, "department") ? openContextMenu : null}
          >
            {state.data.arrondissements.map((arrondissement) => {
              const arrondissementExpanded = state.expanded.arrondissement === arrondissement.gid;

              return (
                <TreeNode
                  key={arrondissement.gid}
                  type="arrondissement"
                  item={arrondissement}
                  level={1}
                  selected={state.selection.arrondissement?.gid === arrondissement.gid}
                  expanded={arrondissementExpanded}
                  hasChildren
                  onClick={() => events.arrondissementSelected.publish(arrondissement)}
                  onContextMenu={canOpenGeographyContext(gui, "arrondissement") ? openContextMenu : null}
                >
                  {state.data.communes.map((commune) => {
                    const communeExpanded = state.expanded.commune === commune.gid;

                    return (
                      <TreeNode
                        key={commune.gid}
                        type="commune"
                        item={commune}
                        level={2}
                        selected={state.selection.commune?.gid === commune.gid}
                        expanded={communeExpanded}
                        hasChildren
                        onClick={() => events.communeSelected.publish(commune)}
                        onContextMenu={canOpenGeographyContext(gui, "commune") ? openContextMenu : null}
                      >
                        {state.data.sections.map((section) => {
                          const sectionExpanded = state.expanded.section === section.gid;

                          return (
                            <TreeNode
                              key={section.gid}
                              type="section"
                              item={section}
                              level={3}
                              selected={state.selection.section?.gid === section.gid}
                              expanded={sectionExpanded}
                              hasChildren
                              onClick={() => events.sectionSelected.publish(section)}
                              onContextMenu={canOpenGeographyContext(gui, "section") ? openContextMenu : null}
                            >
                              {state.data.votingCenters.map((votingCenter) => (
                                <TreeNode
                                  key={votingCenter.gid}
                                  type="voting-center"
                                  item={votingCenter}
                                  level={4}
                                  selected={state.selection.votingCenter?.gid === votingCenter.gid}
                                  expanded={false}
                                  hasChildren={false}
                                  onClick={() => events.votingCenterSelected.publish(votingCenter)}
                                  onContextMenu={canOpenGeographyContext(gui, "voting-center") ? openContextMenu : null}
                                />
                              ))}
                            </TreeNode>
                          );
                        })}
                      </TreeNode>
                    );
                  })}
                </TreeNode>
              );
            })}
          </TreeNode>
        );
      })}
    </>
  );
};

export default AdministrativeTree;
